import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardMedia,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { ArrowBack, ZoomIn, LocationOn } from '@mui/icons-material';
import { APIProvider, Map, AdvancedMarker, Pin, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import axios from '../utils/axios';
import { formatTime12Hour } from '../utils/timeFormat';
import BookingForm from '../components/BookingForm';

const LIBRARIES = ['places', 'geometry'];

const GymDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gym, setGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(true);
  
  const mapContainerStyle = {
    width: '100%',
    height: '350px',
    borderRadius: '8px'
  };

  useEffect(() => {
    const fetchGym = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/gyms/${id}`);
        setGym(response.data);
        setError(null);
        
        const coordinates = response.data.location?.coordinates;
        if (coordinates && Array.isArray(coordinates) && coordinates.length >= 2) {
          setMapPosition({
            lat: coordinates[1], 
            lng: coordinates[0]
          });
        } else {
          console.warn('Invalid location coordinates:', coordinates);
          setMapPosition(null);
        }
      } catch (err) {
        console.error('Error fetching gym:', err);
        setError('Failed to load gym details');
      } finally {
        setLoading(false);
      }
    };

    fetchGym();
  }, [id]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  const handleBookingComplete = (booking) => {
    setShowBookingForm(false);
    alert('Booking created successfully!');
  };

  const handleMarkerClick = () => {
    setInfoWindowOpen(!infoWindowOpen);
  };

  const handleInfoWindowClose = () => {
    setInfoWindowOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !gym) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Gym not found'}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/gyms')}
          >
            Back to Listings
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/gyms')}
        sx={{ mb: 3 }}
      >
        Back to Listings
      </Button>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                height="400"
                image={gym.photos[0] || 'https://via.placeholder.com/800x400?text=No+Image+Available'}
                alt={gym.name}
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
                onClick={() => handleImageClick(gym.photos[0])}
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
                onClick={() => handleImageClick(gym.photos[0])}
              >
                <ZoomIn />
              </IconButton>
            </Box>

            {gym.photos.length > 1 && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {gym.photos.slice(1).map((photo, index) => (
                  <Grid item xs={4} key={index}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.9,
                        },
                      }}
                      onClick={() => handleImageClick(photo)}
                    >
                      <CardMedia
                        component="img"
                        height="140"
                        image={photo}
                        alt={`Gym photo ${index + 2}`}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography variant="h4" gutterBottom>
                {gym.name}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {gym.description}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn color="primary" />
                  Location
                </Typography>
                
                <Box sx={{ height: 350, borderRadius: 1, overflow: 'hidden', mb: 2, border: '2px solid #e0e0e0' }}>
                  {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                    <Box sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: '#f5f5f5'
                    }}>
                      <Typography variant="body2" color="error">
                        Google Maps API key is missing
                      </Typography>
                    </Box>
                  ) : !mapPosition ? (
                    <Box sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: '#f5f5f5'
                    }}>
                      <Typography variant="body2">
                        Location not available
                      </Typography>
                    </Box>
                  ) : (
                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={LIBRARIES}>
                      <Map
                        mapId="gym-location-map"
                        defaultCenter={mapPosition}
                        defaultZoom={18}
                        style={mapContainerStyle}
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                      >
                        <AdvancedMarker 
                          position={mapPosition} 
                          onClick={handleMarkerClick}
                        >
                          <Pin
                            background={'#FF6B35'}
                            borderColor={'#FFFFFF'}
                            glyphColor={'#FFFFFF'}
                          />
                        </AdvancedMarker>
                        
                        {infoWindowOpen && (
                          <InfoWindow
                            position={{
                              lat: mapPosition.lat + 0.0001,
                              lng: mapPosition.lng
                            }}
                            pixelOffset={{ x: 0, y: -40 }}
                            onCloseClick={handleInfoWindowClose}
                          >
                            <Box sx={{ 
                              p: 1.5, 
                              minWidth: 200,
                              maxWidth: 300,
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                              borderRadius: '8px',
                              color: 'white',
                              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
                            }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                  mb: 0.5,
                                  lineHeight: 1.2
                                }}
                              >
                                🏋️ {gym.name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.9rem',
                                  opacity: 0.95,
                                  fontWeight: 500,
                                  mt: 0.5
                                }}
                              >
                                ${gym.pricePerHour}/hr • Max {gym.maxPeople} people
                              </Typography>
                            </Box>
                          </InfoWindow>
                        )}
                      </Map>
                    </APIProvider>
                  )}
                </Box>
                
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa',
                    border: '1px solid #e9ecef'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#495057' }}>
                    📍 {gym.address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Click the marker on the map to toggle gym details
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Amenities
              </Typography>
              <Grid container spacing={2}>
                {gym.amenities && Object.entries(gym.amenities).map(([key, value]) => {
                  const boolValue = typeof value === 'string' ? value === 'true' : Boolean(value);
                  return boolValue && (
                    <Grid item xs={6} sm={4} key={key}>
                      <Typography variant="body2">
                        • {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Available Hours Section */}
              {gym.availableHours && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Available Hours
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(gym.availableHours).map(([day, schedule]) => (
                      <Grid item xs={12} sm={6} md={4} key={day}>
                        <Paper 
                          elevation={1} 
                          sx={{ 
                            p: 1.5, 
                            textAlign: 'center',
                            bgcolor: schedule.enabled ? 'success.50' : 'grey.100',
                            border: schedule.enabled ? '1px solid #4caf50' : '1px solid #e0e0e0'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold', 
                              textTransform: 'capitalize', 
                              mb: 0.5,
                              color: schedule.enabled ? 'success.main' : 'text.secondary'
                            }}
                          >
                            {day}
                          </Typography>
                          {schedule.enabled ? (
                            <Typography variant="body2" color="success.main">
                              {formatTime12Hour(schedule.start)} - {formatTime12Hour(schedule.end)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Closed
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                ${gym.pricePerHour}/hr
              </Typography>
              {gym.amenities?.personalTraining && gym.personalTrainingPremium > 0 && (
                <Typography variant="body2" color="success.main" gutterBottom>
                  + ${gym.personalTrainingPremium}/hr for personal training
                </Typography>
              )}
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Maximum {gym.maxPeople} people
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => setShowBookingForm(true)}
                sx={{ mt: 2 }}
              >
                Book Now
              </Button>
            </Paper>

            {showBookingForm && (
              <BookingForm
                gym={gym}
                onBookingComplete={handleBookingComplete}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(selectedImage)}
        onClose={handleCloseImage}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default GymDetails;
